// src/pages/AuthHome.tsx
import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import { Loading } from "@carbon/react";
import Login from "../../components/Login";
import Profile from "../../components/Profile";
import { Layout } from "../../layouts";
import { useSupabaseUser } from "../../hooks/useSupabaseUser";
import { establishApiSession } from "../../services/apiSession";
import { getSafeRedirectTarget } from "../../utils/redirect";

import styles from "./HomePage.module.scss";

const HomePage = () => {
  const { user, loading, setUser } = useSupabaseUser();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash.startsWith("#") ? location.hash.slice(1) : location.hash;
    const qs = new URLSearchParams(hash);
    const type = qs.get("type");

    if (type === "invite") {
      // Keep the original search and hash so /set-password can read tokens
      navigate({ pathname: "/set-password", search: location.search, hash: location.hash }, { replace: true });
    }
  }, [navigate, location.search, location.hash]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const redirectTo = params.get("redirect_to");

    // Only act if user was redirected here intentionally
    if (!redirectTo) return;

    // Wait until auth state is resolved
    if (loading) return;

    if (user) {
      void (async () => {
        try {
          await establishApiSession();
        } catch {
          return;
        }

        const safeRedirect = getSafeRedirectTarget(redirectTo);
        const url = new URL(safeRedirect, window.location.origin);

        if (url.pathname !== window.location.pathname || url.search !== window.location.search || url.hash !== window.location.hash) {
          window.location.href = url.toString();
        }
      })();
    }
  }, [user, loading, location.search]);

  return (
    <Layout>
      {loading ? (
        <div className={styles.spinner}>
          <Loading description="Loading user session..." />
        </div>
      ) : user ? (
        <Profile name={user.user_metadata.full_name} email={user.email ?? "Unknown"} onLogout={() => setUser(null)} />
      ) : (
        <Login onLoginSuccess={(u) => setUser(u)} />
      )}
    </Layout>
  );
};

export default HomePage;
